import React, { useEffect, useState } from "react";
import { Button, Form, Input, Spin, Typography } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import message from "@/utils/globalMessage";
import { api } from "@/redux/api";
import ResponsiveSelect from "@components/ResponsiveSelect";
import { SERVICE_REQUEST_PRIORITIES } from "@/containers/service-request/constants";
import {
  CreateHeader,
  CreatePage,
  FormActions,
  FormCard,
  FormPanel,
} from "./TenantServiceRequestCreate.styles";

const PRIORITY_LABELS = {
  Low: "Low - Not urgent",
  Important: "Important - Needs attention",
  Urgent: "Urgent - Critical issue",
};

const getResponseRows = (response) => {
  const payload = response?.data;
  return Array.isArray(payload) ? payload : payload?.results || [];
};

export default function TenantServiceRequestCreatePage() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const [facilities, setFacilities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const listPath = "/tennant/service-request";

  useEffect(() => {
    let active = true;

    Promise.all([api.getTenantFacilities(), api.getTenantServiceCategories()])
      .then(([facilityResponse, categoryResponse]) => {
        if (!active) return;

        const facilityRows = getResponseRows(facilityResponse);
        const categoryRows = getResponseRows(categoryResponse);
        setFacilities(facilityRows);
        setCategories(categoryRows);

        const facilityId = facilityRows.length === 1 ? facilityRows[0].id : undefined;
        form.setFieldsValue({ facility: facilityId, priority: "Low" });
      })
      .catch(() => {
        if (active) message.error("Failed to load request options");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [form]);

  const submit = async (values) => {
    setSubmitting(true);
    try {
      const response = await api.createTenantServiceRequest(values);
      message.success("Service request created successfully");
      navigate(`/tennant/service-request-detail/${response.data.id}`, { replace: true });
    } catch (error) {
      message.error("Failed to create service request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CreatePage>
      {isPC ? (
        <CreateHeader>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(listPath)}
            title="Back"
            aria-label="Back"
          />
          <Typography.Title level={4}>Create Service Request</Typography.Title>
        </CreateHeader>
      ) : null}

      <Spin spinning={loading}>
        <FormPanel>
          <FormCard>
            <Form form={form} layout="vertical" onFinish={submit} autoComplete="off" preserve>
              <Form.Item
                label="Facility"
                name="facility"
                rules={[{ required: true, message: "Please select a facility" }]}
              >
                <ResponsiveSelect
                  placeholder="Select a facility"
                  popupTitle="Select Facility"
                  showSearch
                  options={facilities.map((item) => ({ value: item.id, label: item.name }))}
                />
              </Form.Item>
              <Form.Item
                label="Suite / Area"
                name="suite_area"
                rules={[
                  { required: true, message: "Please enter Suite / Area" },
                  { whitespace: true, message: "Please enter Suite / Area" },
                ]}
              >
                <Input maxLength={255} placeholder="e.g. Suite 301 or West Lobby" />
              </Form.Item>
              <Form.Item
                label="Service Category"
                name="category"
                rules={[{ required: true, message: "Please select a service category" }]}
              >
                <ResponsiveSelect
                  placeholder="Select a service category"
                  popupTitle="Select Category"
                  showSearch
                  options={categories.map((item) => ({ value: item.id, label: item.name }))}
                />
              </Form.Item>
              <Form.Item
                label="Title / Issue"
                name="title"
                rules={[
                  { required: true, message: "Please enter a title / issue" },
                  { whitespace: true, message: "Please enter a title / issue" },
                ]}
              >
                <Input maxLength={255} placeholder="e.g. AC not cooling in Suite 301" />
              </Form.Item>
              <Form.Item
                label="Priority"
                name="priority"
                rules={[{ required: true, message: "Please select a priority" }]}
              >
                <ResponsiveSelect
                  placeholder="Select a priority"
                  popupTitle="Select Priority"
                  options={SERVICE_REQUEST_PRIORITIES.map((value) => ({
                    value,
                    label: PRIORITY_LABELS[value] || value,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[
                  { required: true, message: "Please enter a description" },
                  { whitespace: true, message: "Please enter a description" },
                ]}
              >
                <Input.TextArea
                  rows={6}
                  placeholder="Describe the issue, when it started, and any relevant context"
                />
              </Form.Item>

              <FormActions>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(listPath)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  Create Request
                </Button>
              </FormActions>
            </Form>
          </FormCard>
        </FormPanel>
      </Spin>
    </CreatePage>
  );
}
